import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { ChevronDown } from 'lucide-react';
import { API_BASE } from '../helpers';

/**
 * DMVendorPicker — a self-contained searchable dropdown for choosing a
 * vendor contact. Hand-built with React state (no new dependency).
 *
 * Vendors are loaded lazily on first open from the existing CRM contacts
 * endpoint (GET /api/contacts has no server-side filtering, so we filter
 * client-side for ContactType === 'Vendor' and not archived) and cached
 * for the component's lifetime. The Azure AD bearer token is attached by
 * the global axios interceptor configured in App.js.
 *
 * Props:
 *   value    — { contactId, name, email } | null  (selected vendor)
 *   onChange — (vendor | null) => void
 */
function DMVendorPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [vendors, setVendors] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef(null);
  const searchRef = useRef(null);

  // Lazy-load the vendor list once, on first open.
  useEffect(() => {
    if (!open || vendors !== null || loading) return;
    setLoading(true);
    setLoadError(null);
    axios.get(`${API_BASE}/contacts`)
      .then(res => {
        const list = (res.data || [])
          .filter(c => c.ContactType === 'Vendor' && !c.IsArchived)
          .map(c => ({
            contactId: c.ContactId,
            name: `${c.FirstName || ''} ${c.LastName || ''}`.trim() || c.CompanyName || 'Unnamed',
            email: c.Email || '',
            company: c.CompanyName || '',
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setVendors(list);
      })
      .catch(() => setLoadError('Could not load vendors. Please try again.'))
      .finally(() => setLoading(false));
  }, [open, vendors, loading]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus the search box when the panel opens.
  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const filtered = useMemo(() => {
    const list = vendors || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(v =>
      v.name.toLowerCase().includes(q) || v.email.toLowerCase().includes(q)
    );
  }, [vendors, search]);

  // Keep the highlighted row in range as the filtered set changes.
  useEffect(() => { setActiveIndex(0); }, [search, vendors]);

  const selectVendor = (vendor) => {
    onChange(vendor);
    setOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) selectVendor(filtered[activeIndex]);
    }
  };

  return (
    <div className="dm-vpicker" ref={rootRef}>
      <button
        type="button"
        className={`dm-vpicker__control ${open ? 'dm-vpicker__control--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`dm-vpicker__value ${value ? '' : 'dm-vpicker__value--placeholder'}`}>
          {value ? `${value.name}${value.email ? ` (${value.email})` : ''}` : 'Select a vendor...'}
        </span>
        <ChevronDown size={16} />
      </button>

      {open && (
        <div className="dm-vpicker__panel">
          <input
            ref={searchRef}
            type="text"
            className="dm-vpicker__search"
            placeholder="Search vendors by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="dm-vpicker__list" role="listbox">
            {loading && <div className="dm-vpicker__empty">Loading vendors...</div>}
            {loadError && !loading && <div className="dm-vpicker__empty">{loadError}</div>}
            {!loading && !loadError && filtered.length === 0 && (
              <div className="dm-vpicker__empty">No vendors found</div>
            )}
            {!loading && !loadError && filtered.map((v, i) => (
              <button
                type="button"
                key={v.contactId}
                className={`dm-vpicker__option ${i === activeIndex ? 'dm-vpicker__option--active' : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => selectVendor(v)}
                role="option"
                aria-selected={value?.contactId === v.contactId}
              >
                <span className="dm-vpicker__option-name">
                  {v.name}{v.company ? ` — ${v.company}` : ''}
                </span>
                <span className="dm-vpicker__option-email">{v.email || 'No email on file'}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DMVendorPicker;
